/**
 * @element qds-empty-state
 *
 * A message component shown where content would be — for no data, no filter
 * results, no permission, a load failure, or a cleared work queue. NOT for
 * loading states; use a skeleton or spinner for those.
 *
 * @attr {string} variant - One of "empty", "no-results", "error", "no-access", "done". Default "empty". Sets the default icon and icon colour only; never supplies copy.
 * @attr {string} icon - A qds-icon-picker value (e.g. "prime:pi-inbox" or "lucide:inbox"); overrides the variant default icon. Legacy `pi-*` values are supported.
 * @attr {string} title-text - The title text. Required in practice; warns via console.debug when empty. (The global `title` attribute is reserved.)
 * @attr {string} description - The description text below the title.
 * @attr {string} size - One of "sm", "md", "lg". Default "md". "sm" for inside a select listbox, "lg" for a full page.
 * @attr {boolean} compact - When present, renders icon and title on one line. For table rows.
 *
 * @csspart container - The outer container.
 * @csspart icon - The icon wrapper.
 * @csspart title - The title element.
 * @csspart description - The description element.
 * @csspart actions - The actions slot wrapper.
 *
 * @slot default - Replaces the description text with rich content.
 * @slot action - Primary and secondary actions, rendered below the description.
 * @slot icon - Replaces the default icon entirely, for a custom SVG.
 *
 * @example
 * <qds-empty-state
 *   variant="empty"
 *   title-text="No accounts yet"
 *   description="Accounts appear here once your first portfolio is linked.">
 *   <qds-button slot="action">Link account</qds-button>
 * </qds-empty-state>
 */

import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons, renderQdsIcon } from '../shared/icon-utils.js';

/** Variant → default PrimeIcons class. */
const VARIANT_ICONS: Readonly<Record<string, string>> = {
  'empty': 'pi-inbox',
  'no-results': 'pi-search',
  'error': 'pi-exclamation-triangle',
  'no-access': 'pi-lock',
  'done': 'pi-check-circle',
};

const VALID_VARIANTS = ['empty', 'no-results', 'error', 'no-access', 'done'];
const VALID_SIZES = ['sm', 'md', 'lg'];

/** Escapes user-supplied text so it can be safely interpolated into innerHTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class QdsEmptyState extends HTMLElement {
  private _variant: string = 'empty';
  private _icon: string = '';
  private _titleText: string = '';
  private _description: string = '';
  private _size: string = 'md';
  private _compact: boolean = false;

  static get observedAttributes(): string[] {
    return ['variant', 'icon', 'title-text', 'description', 'size', 'compact'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._readAttributes();
    this.render();
  }

  disconnectedCallback(): void {
    // Display-only component — no listeners to clean up.
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'variant':
        this._variant = newValue && VALID_VARIANTS.includes(newValue) ? newValue : 'empty';
        break;
      case 'icon':
        this._icon = newValue || '';
        break;
      case 'title-text':
        this._titleText = newValue || '';
        break;
      case 'description':
        this._description = newValue || '';
        break;
      case 'size':
        this._size = newValue && VALID_SIZES.includes(newValue) ? newValue : 'md';
        break;
      case 'compact':
        this._compact = newValue !== null;
        break;
    }

    if (this.isConnected) {
      this.render();
    }
  }

  /** Reads all observed attributes into private fields. Called on connect. */
  private _readAttributes(): void {
    const variant = this.getAttribute('variant');
    this._variant = variant && VALID_VARIANTS.includes(variant) ? variant : 'empty';
    this._icon = this.getAttribute('icon') || '';
    this._titleText = this.getAttribute('title-text') || '';
    this._description = this.getAttribute('description') || '';
    const size = this.getAttribute('size');
    this._size = size && VALID_SIZES.includes(size) ? size : 'md';
    this._compact = this.hasAttribute('compact');
  }

  /** Returns the icon class: explicit `icon` attribute, or the variant default. */
  private _getEffectiveIcon(): string {
    return this._icon || VARIANT_ICONS[this._variant] || 'pi-inbox';
  }

  /** no-results is announced; everything else is a passive note. */
  private _getRole(): string {
    return this._variant === 'no-results' ? 'status' : 'note';
  }

  /** Only no-results gets a live region. */
  private _getAriaLive(): string | null {
    return this._variant === 'no-results' ? 'polite' : null;
  }

  private render(): void {
    if (!this.shadowRoot) return;

    if (!this._titleText) {
      console.debug(
        'qds-empty-state: title-text is empty — an empty state should always explain what happened.',
      );
    }

    const iconClass = this._getEffectiveIcon();
    const role = this._getRole();
    const ariaLive = this._getAriaLive();
    const ariaLiveAttr = ariaLive ? ` aria-live="${ariaLive}"` : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          --qds-empty-state-padding: var(--theme-space-6, 24px);
          --qds-empty-state-icon-size: 32px;
          --qds-empty-state-max-width: 40ch;
          --qds-empty-state-gap: var(--theme-space-3, 12px);
        }

        :host([size="sm"]) {
          --qds-empty-state-padding: var(--theme-space-3, 12px);
          --qds-empty-state-icon-size: 20px;
          --qds-empty-state-gap: var(--theme-space-2, 8px);
        }

        :host([size="lg"]) {
          --qds-empty-state-padding: var(--theme-space-8, 32px);
          --qds-empty-state-icon-size: 48px;
          --qds-empty-state-gap: var(--theme-space-4, 16px);
        }

        .container {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: var(--qds-empty-state-padding);
          gap: var(--qds-empty-state-gap);
        }

        :host([compact]) .container {
          align-items: flex-start;
          text-align: left;
        }

        .header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--qds-empty-state-gap);
        }

        :host([compact]) .header {
          flex-direction: row;
          align-items: center;
          gap: var(--theme-space-2, 8px);
        }

        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          color: var(--theme-text-tertiary);
        }

        .icon i {
          font-size: var(--qds-empty-state-icon-size);
          color: var(--theme-text-tertiary);
        }

        :host([variant="error"]) .icon,
        :host([variant="error"]) .icon i {
          color: var(--theme-error);
        }

        :host([variant="done"]) .icon,
        :host([variant="done"]) .icon i {
          color: var(--theme-success);
        }

        .title {
          color: var(--theme-text-primary);
          font-size: 16px;
          font-weight: 600;
          line-height: 1.4;
          margin: 0;
        }

        .description {
          color: var(--theme-text-secondary);
          font-size: 14px;
          line-height: 1.5;
          max-width: var(--qds-empty-state-max-width);
          text-wrap: pretty;
          margin: 0;
        }

        .actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--theme-space-2, 8px);
          margin-top: var(--theme-space-4, 16px);
        }

        :host([compact]) .actions {
          flex-direction: row;
          align-items: center;
          margin-top: var(--theme-space-2, 8px);
        }

        .actions ::slotted(*) {
          margin: 0;
        }
      </style>

      <div part="container" class="container" role="${role}"${ariaLiveAttr}>
        <div class="header">
          <div part="icon" class="icon" aria-hidden="true">
            <slot name="icon">
              ${renderQdsIcon(iconClass)}
            </slot>
          </div>
          <div part="title" class="title">${escapeHtml(this._titleText)}</div>
        </div>
        <div part="description" class="description">
          <slot>${escapeHtml(this._description)}</slot>
        </div>
        <div part="actions" class="actions">
          <slot name="action"></slot>
        </div>
      </div>
    `;

    void adoptPrimeIconsStyles(this.shadowRoot);
    hydrateQdsLucideIcons(this.shadowRoot);
  }
}

if (!customElements.get('qds-empty-state')) {
  customElements.define('qds-empty-state', QdsEmptyState);
}
